import React, { useEffect, useState } from "react";
import { listAuditLogs } from "../api/agentApi";
import { useUser } from "./UserContext";
import { DetailsCell } from "./DetailsCell";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Alert,
  Button,
  TextField,
  Tooltip,
  Chip
} from "@mui/material";

function AuditLogsPage() {
  const { user } = useUser();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (user?.role !== "admin") return;
    setLoading(true);
    // Only apply filters if at least one filter field has 3+ chars
    const activeFilters = {};
    ["username", "action", "status"].forEach(key => {
      if (filters[key]) {
        // Use wildcard matching for substring search
        activeFilters[key] = `%${filters[key]}%`;
      }
    });
    listAuditLogs({ ...activeFilters, page, pageSize })
      .then((res) => {
        setLogs(res.logs);
        setTotal(res.total);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to load audit logs");
        setLoading(false);
      });
  }, [filters, page, pageSize, user]);

  if (user?.role !== "admin") {
    return (<Box sx={{ p: 3 }}><Alert severity="error">Access denied. Admins only.</Alert></Box>);
  }

  return (
    <Box sx={{ pt:1, px: 3 }}>
       <Toolbar disableGutters sx={{ display: "flex", alignItems: "center"}}>
        <Typography variant="h4">Audit Logs</Typography>
      </Toolbar>
      {/* Filter UI */}
      <Box sx={{ mx: "auto", mb: 2, mt: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Filter Audit Logs</Typography>
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            bgcolor: "#f8f9fa",
            border: "1px solid #e0e0e0",
            boxShadow: 0,
            mb: 2
          }}
          elevation={0}
        >
          <TextField
            label="Username"
            size="small"
            value={filters.username || ""}
            onChange={e => setFilters(f => ({ ...f, username: e.target.value }))}
            sx={{ width: 160, flex: "1 1 160px", bgcolor: '#fff' }}
          />
          <TextField
            label="Action"
            size="small"
            value={filters.action || ""}
            onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
            sx={{ width: 160, flex: "1 1 160px", bgcolor: '#fff' }}
          />
          <TextField
            label="Status"
            size="small"
            value={filters.status || ""}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            sx={{ width: 160, flex: "1 1 160px", bgcolor: '#fff' }}
          />
        </Paper>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Target ID</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">No audit logs found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map(log => (
                  <TableRow key={log.id} hover sx={{ fontSize: "0.95rem" }}>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>
                      <Tooltip title={log.timestamp}><span>{log.timestamp}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>{log.username}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>{log.action}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>
                      <Chip
                        label={log.status}
                          sx={{
                            bgcolor: log.status === "success"
                            ? "#43a047"
                            : log.status === "failed"
                              ? "#d32f2f"
                              : log.status === "started"
                                ? "#1976d2"
                                : "#e0e0e0",
                            color: log.status === "success" ? "#fff" : log.status === "failed" ? "#fff" : "#333",
                            fontWeight: 500,
                            borderRadius: "9999px", // fully rounded
                            height: 24,
                            px: 1.5,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>{log.target_id}</TableCell>
                    <DetailsCell details={log.details} />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {/* Pagination */}
      <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
        <Button disabled={page === 1} onClick={() => setPage(p => p - 1)} variant="outlined">Prev</Button>
        <Typography variant="body2" sx={{ mx: 2 }}>
          Page {page} of {Math.ceil(total / pageSize) || 1}
        </Typography>
        <Button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} variant="outlined">Next</Button>
      </Box>
    </Box>
  );
}

export default AuditLogsPage;
